import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid, Skeleton } from "@mui/material"
import { DateRangePicker, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosResponse } from "axios"
import type { Moment } from "moment"
import moment from "moment"

import { KonanAPI } from "../../../services/KonanAPI"
import type { DeploymentPredictionsSummary } from "../../../types/generated/api/DeploymentPredictionsSummary"
import { KonanTimeHelper } from "../../../utils/genericHelpers"
import type { LiveDataViewProps } from "../Interfaces"

/**
 * Simulation live model's data screen
 * @param {(date: Moment | undefined) => void} setFormikStartDate
 * @param {(date: Moment | undefined) => void} setFormikEndDate
 * @param {(date: number) => void} setFormikRequestsNumber
 * @return {React.ReactElement}
 */
export function LiveDataView(props: Readonly<LiveDataViewProps>): React.ReactElement {
  const { setFormikStartDate, setFormikEndDate, setFormikRequestsNumber } = props

  const { id: projectId } = useParams<{ id: string }>()

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationCreationDialog.liveDataView",
  })

  const [startDate, setStartDate] = useState<moment.Moment | null>(moment().subtract(29, "days"))
  const [endDate, setEndDate] = useState<moment.Moment | null>(moment())

  const konanTime = new KonanTimeHelper()

  // fetching api requests to extract # feedback adn # requests
  const { isLoading: isApiRequestsLoading, data: apiRequests } = useQuery<AxiosResponse<DeploymentPredictionsSummary>>(
    ["apiRequests", projectId, startDate, endDate],
    () =>
      KonanAPI.fetchApiRequestsSummary({
        project_uuid: projectId as string,
        start_date: startDate?.toISOString() as string,
        end_date: endDate?.toISOString() as string,
      }),
    {
      onSuccess: (response) => {
        setFormikRequestsNumber(response.data.total)
      },
      enabled: !!projectId && !!startDate && !!endDate,
    },
  )

  return (
    <Grid container spacing={0.5}>
      <Grid item xs={12}>
        <Typography variant="label-bold" variantColor={2}>
          {t("title")}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={(startDate) => {
            setStartDate(startDate)
            setFormikStartDate(konanTime.adjustDate(startDate, "start") as Moment)
          }}
          onEndDateChange={(endDate) => {
            setEndDate(endDate)
            setFormikEndDate(konanTime.adjustDate(endDate, "end") as Moment)
          }}
          disableFuture
        />
      </Grid>

      <Grid item xs={12}>
        {isApiRequestsLoading ? (
          <Skeleton animation="wave" width="30%" height="60%" />
        ) : (
          <Typography variant="label-regular">
            {t("requestsAndFeedbackText", {
              requests: apiRequests?.data.total,
              feedback: apiRequests?.data.feedback_count,
            })}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} mt={1}>
        {0 === apiRequests?.data.feedback_count && !isApiRequestsLoading && (
          <Snackbar
            severity="warning"
            fullWidth
            title={t("noFeedbackSnackbar.title")}
            description={t("noFeedbackSnackbar.description")}
          />
        )}
      </Grid>
    </Grid>
  )
}
